import { audio } from '../game/AudioManager.js';
import { gameState } from '../game/GameState.js';
import { questManager } from '../game/QuestManager.js';
import { WindowManager } from './WindowManager.js';
import { HeartMiniGame } from './HeartMiniGame.js';
import { STORY_CANON } from '../data/story.js';

export class DesktopSimulation {
  constructor(rootContainer, onLevelComplete) {
    this.root = rootContainer;
    this.onLevelComplete = onLevelComplete;
    this.container = null;
    this.winManager = null;
    this.friendAccepted = false;
    this.emilyActive = false;
    this.chatStep = 0; // 0: unread, 1: Emily sent 'Hii talha', 2: Talha replied
  }

  mount() {
    this.container = document.createElement('div');
    this.container.id = 'desktop-container';
    this.container.innerHTML = `
      <!-- Windows 7 Login Screen -->
      <div id="win-login" class="win-login-screen">
        <div class="win-user-card">
          <img src="/assets/characters/talha-2016/portrait.png" class="win-avatar" alt="Talha" />
          <div class="win-username">Talha</div>
          <div class="win-pass-box">
            <input type="password" id="login-pass" class="win-input" placeholder="Password" />
            <button id="login-btn" class="win-btn">➔</button>
          </div>
          <div class="win-hint">Hint: "The story began on a rooftop." (password: rooftop)</div>
        </div>
      </div>

      <!-- Desktop Icons -->
      <div class="desktop-icons-area">
        <div class="desktop-shortcut" id="sc-browser">
          <div style="font-size:36px;">🌐</div>
          <div class="desktop-shortcut-label">FaceSpace</div>
        </div>
        <div class="desktop-shortcut" id="sc-messenger">
          <div style="font-size:36px;">💬</div>
          <div class="desktop-shortcut-label">Messenger</div>
        </div>
        <div class="desktop-shortcut" id="sc-heart">
          <div style="font-size:36px;">💖</div>
          <div class="desktop-shortcut-label">Heart.exe</div>
        </div>
        <div class="desktop-shortcut" id="sc-pictures">
          <div style="font-size:36px;">🖼️</div>
          <div class="desktop-shortcut-label">My Pictures</div>
        </div>
      </div>

      <!-- Taskbar -->
      <div class="win-taskbar">
        <div class="win-start-btn" id="start-btn">
          <svg width="18" height="18" viewBox="0 0 16 16" style="margin-right:4px; filter: drop-shadow(0 1px 1px rgba(0,0,0,0.5));">
            <path d="M0 2.2L6.4 1.3v6.1H0V2.2zm0 6.8h6.4v6.1L0 14.2V9zm7.6-7.9L16 0v7.4H7.6V1.1zm8.4 7.9v7.4l-8.4-1.1V9H16z" fill="#fff"/>
          </svg>
          start
        </div>
        <div class="win-taskbar-tasks" id="taskbar-tasks"></div>
        <div class="win-tray">
          <div id="tray-time">2:25 PM</div>
          <div style="font-size:10px;">9/20/2016</div>
        </div>
      </div>
    `;

    this.root.appendChild(this.container);
    this.winManager = new WindowManager(this.container);

    this.setupLogin();
    this.setupShortcuts();
  }

  setupLogin() {
    const loginScreen = this.container.querySelector('#win-login');
    const passInput = this.container.querySelector('#login-pass');
    const loginBtn = this.container.querySelector('#login-btn');

    const doLogin = () => {
      const val = passInput.value.trim().toLowerCase();
      if (val === 'rooftop' || val === 'admin' || val === '') {
        audio.playNotification();
        loginScreen.classList.add('hidden');
        questManager.setQuest("Open FaceSpace browser to check notifications");
        setTimeout(() => this.openFaceSpace(), 600);
      } else {
        alert("Incorrect password. Hint: rooftop");
      }
    };

    loginBtn.addEventListener('click', doLogin);
    passInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') doLogin();
    });
  }

  setupShortcuts() {
    this.container.querySelector('#sc-browser').addEventListener('click', () => this.openFaceSpace());
    this.container.querySelector('#sc-messenger').addEventListener('click', () => this.openMessenger());
    this.container.querySelector('#sc-heart').addEventListener('click', () => this.openHeartGame());
    this.container.querySelector('#sc-pictures').addEventListener('click', () => this.openPictures());
  }

  openFaceSpace() {
    const content = `
      <div class="fs-navbar">
        <div class="fs-logo">facespace</div>
        <input type="text" class="fs-search" placeholder="Search people..." value="Labonno" />
        <div style="font-size:14px;">Talha | Home | Find Friends</div>
      </div>
      <div class="fs-content">
        <div class="fs-sidebar">
          <b>Favorites</b>
          <div>📰 News Feed</div>
          <div>💬 Messages</div>
          <div>👥 Friends (1)</div>
        </div>
        <div class="fs-main-feed">
          <div class="fs-card" id="req-card">
            <h4>Friend Request</h4>
            <hr style="margin:8px 0; border:0; border-top:1px solid #eee;" />
            <div class="fs-profile-header">
              <img src="/assets/characters/emily-2016/portrait.png" class="fs-profile-img" alt="Labonno" />
              <div>
                <h3 style="color:#3b5998;">Labonno (Emily)</h3>
                <p style="color:#777; font-size:13px; margin:4px 0;">Found via People You May Know • 2 mutual friends</p>
                <div style="display:flex; gap:8px; margin-top:8px;">
                  <button id="btn-confirm-req" class="fs-confirm-btn">${this.friendAccepted ? 'Friends ✓' : 'Confirm'}</button>
                  <button id="btn-view-msg" style="background:#eee; border:1px solid #ccc; padding:6px 12px; border-radius:3px; cursor:pointer;">Send Message</button>
                </div>
              </div>
            </div>
            <div id="req-success-text" style="color:#2b825b; font-weight:600; margin-top:10px; display:${this.friendAccepted ? 'block' : 'none'};">
              You and Labonno are now friends!
            </div>
          </div>
        </div>
      </div>
    `;

    const win = this.winManager.createWindow('facespace', 'FaceSpace - Internet Explorer', 680, 460, content);
    const confirmBtn = win.querySelector('#btn-confirm-req');
    const successText = win.querySelector('#req-success-text');

    confirmBtn.addEventListener('click', () => {
      if (!this.friendAccepted) {
        this.friendAccepted = true;
        confirmBtn.innerText = 'Friends ✓';
        confirmBtn.style.background = '#2b825b';
        successText.style.display = 'block';
        audio.playRomanticChime();
        questManager.setQuest("Open Messenger or play Heart.exe while waiting for Labonno");
        setTimeout(() => {
          this.openMessenger();
        }, 1200);
      }
    });

    win.querySelector('#btn-view-msg').addEventListener('click', () => {
      this.openMessenger();
    });
  }

  openMessenger() {
    const content = `
      <div class="msg-container">
        <div class="msg-header">
          <img src="/assets/characters/emily-2016/portrait.png" style="width:36px; height:36px; border-radius:50%; object-fit:cover; object-position:center; border:2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.2);" />
          <div style="flex:1;">
            <b style="font-size:14px; color:#1f2937;">Labonno</b>
            <div style="font-size:11px; display:flex; align-items:center; gap:4px; color:#6b7280;">
              <span id="msg-dot" class="msg-status-dot ${this.emilyActive ? '' : 'away'}"></span>
              <span id="msg-status-text">${this.emilyActive ? 'Active now' : 'Last active earlier'}</span>
            </div>
          </div>
        </div>
        <div class="msg-chat-history" id="chat-history">
          <div class="msg-date-pill">
            SEP 20, 2016 AT 2:25 PM
          </div>
        </div>
        <div class="msg-input-area">
          <input type="text" id="msg-input" class="msg-input" placeholder="Type a message..." />
          <button id="msg-send-btn" class="msg-send-btn">Send</button>
        </div>
        <div id="suggested-reply-area" style="padding:8px 12px; background:rgba(255,255,255,0.92); border-top:1px solid #fed7e2; display:none;">
          <button id="btn-quick-reply" style="background:#fce7f3; color:#db2777; border:1px solid #f472b6; border-radius:16px; padding:6px 16px; font-weight:600; font-size:13px; cursor:pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05);">Reply: "Hii talha"</button>
        </div>
      </div>
    `;

    const win = this.winManager.createWindow('messenger', 'Messenger - Labonno', 420, 500, content);
    const history = win.querySelector('#chat-history');
    const input = win.querySelector('#msg-input');
    const sendBtn = win.querySelector('#msg-send-btn');
    const dot = win.querySelector('#msg-dot');
    const statusText = win.querySelector('#msg-status-text');
    const quickArea = win.querySelector('#suggested-reply-area');
    const quickBtn = win.querySelector('#btn-quick-reply');

    if (this.emilyActive && this.chatStep === 0) {
      this.deliverEmilyMessage(history, quickArea);
    }

    const sendTalhaReply = () => {
      const txt = input.value.trim() || "Hii talha";
      const b = document.createElement('div');
      b.className = 'msg-bubble talha';
      b.innerText = txt;
      history.appendChild(b);
      input.value = '';
      quickArea.style.display = 'none';
      history.scrollTop = history.scrollHeight;
      audio.playNotification();
      audio.playRomanticChime();

      questManager.setQuest("A 10-Year journey has begun...");
      setTimeout(() => {
        this.closeAllAndProceed();
      }, 2500);
    };

    sendBtn.addEventListener('click', sendTalhaReply);
    quickBtn.addEventListener('click', sendTalhaReply);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') sendTalhaReply();
    });
  }

  deliverEmilyMessage(history, quickArea) {
    setTimeout(() => {
      audio.playNotification();
      const row = document.createElement('div');
      row.className = 'msg-row-emily';
      row.innerHTML = `
        <img src="/assets/characters/emily-2016/portrait.png" alt="Labonno" />
        <div class="msg-bubble emily">Hii 😊</div>
      `;
      history.appendChild(row);
      this.chatStep = 1;
      quickArea.style.display = 'block';
      history.scrollTop = history.scrollHeight;
      questManager.setQuest("Reply to Labonno's message ('Hii talha')");
    }, 600);
  }

  openHeartGame() {
    const win = this.winManager.createWindow('heart_exe', 'HEART.EXE', 520, 440, '');
    const body = win.querySelector('.win-body');
    new HeartMiniGame(body, () => {
      this.emilyActive = true;
      audio.playNotification();
      this.openMessenger();
      // Update Messenger status
      const msgWin = this.container.querySelector('#win-messenger');
      if (msgWin) {
        msgWin.querySelector('#msg-dot')?.classList.remove('away');
        const st = msgWin.querySelector('#msg-status-text');
        if (st) st.innerText = 'Active now';
        const hist = msgWin.querySelector('#chat-history');
        const qa = msgWin.querySelector('#suggested-reply-area');
        if (hist && qa && this.chatStep === 0) {
          this.deliverEmilyMessage(hist, qa);
        }
      }
    });
  }

  openPictures() {
    const content = `
      <div style="padding:16px; background:#fff; height:100%;">
        <h4>Saved Memories (2011 - 2016)</h4>
        <div style="display:flex; gap:12px; margin-top:12px;">
          <div style="border:1px solid #ccc; padding:6px; text-align:center;">
            <img src="/assets/characters/emily-2016/portrait.png" style="width:100px; height:100px; object-fit:cover;" />
            <div style="font-size:12px; margin-top:4px;">Labonno.png</div>
          </div>
          <div style="border:1px solid #ccc; padding:6px; text-align:center;">
            <img src="/assets/characters/talha-2016/portrait.png" style="width:100px; height:100px; object-fit:cover;" />
            <div style="font-size:12px; margin-top:4px;">Talha.png</div>
          </div>
        </div>
      </div>
    `;
    this.winManager.createWindow('pictures', 'My Pictures', 420, 280, content);
  }

  closeAllAndProceed() {
    this.container.classList.add('hidden');
    if (this.onLevelComplete) {
      this.onLevelComplete();
    }
  }

  destroy() {
    if (this.container) {
      this.container.remove();
    }
  }
}
